import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { resolveSession } from '@/api/publicSession'
import { useJoinStore } from '@/store/joinStore'
import { useJoinEffectiveLanguage } from '@/hooks/useJoinEffectiveLanguage'
import { t } from '@/i18n/translations'

export function JoinResolver() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const [search] = useSearchParams()
  const token = search.get('t') ?? ''
  const navigate = useNavigate()
  const setResolved = useJoinStore((s) => s.setResolved)
  const setToken = useJoinStore((s) => s.setToken)
  const reset = useJoinStore((s) => s.reset)
  const lang = useJoinEffectiveLanguage()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const ranRef = useRef(false)

  useEffect(() => {
    if (!sessionId || !token) {
      navigate(`/j/${sessionId ?? ''}/status`, {
        replace: true,
        state: { variant: 'link_invalid' },
      })
      return
    }
    if (ranRef.current) return  // guard against StrictMode double-invoke
    ranRef.current = true

    reset()
    // Preserve the raw token so a later abort can re-open this same link.
    setToken(token)
    resolveSession(sessionId, token).then((outcome) => {
      switch (outcome.kind) {
        case 'created':
          setResolved(outcome.data)
          {
            const langs = outcome.data.candidate.availableLanguages
            const avatars = outcome.data.avatar.avatars
            const needsChoice = langs.length > 1 || avatars.length > 1
            navigate(
              needsChoice ? `/j/${sessionId}/preferences` : `/j/${sessionId}/setup`,
              { replace: true },
            )
          }
          break
        case 'terminal':
          navigate(`/j/${sessionId}/status`, {
            replace: true,
            state: {
              variant: outcome.data.status,
              occurredAt: outcome.data.occurredAt,
              errorCode: outcome.data.errorCode,
            },
          })
          break
        case 'in_progress':
          navigate(`/j/${sessionId}/status`, {
            replace: true,
            state: { variant: 'in_progress' },
          })
          break
        case 'not_found':
          navigate(`/j/${sessionId}/status`, {
            replace: true,
            state: { variant: 'link_invalid' },
          })
          break
        case 'network_error':
          setErrorMessage(outcome.message)
          break
      }
    })
  }, [sessionId, token, navigate, reset, setResolved, setToken])

  if (errorMessage) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-lg">{t(lang, 'connectionFailedTitle')}</p>
        <p className="text-sm text-neutral-400">{errorMessage}</p>
        <button onClick={() => location.reload()} className="rounded bg-neutral-800 px-4 py-2">
          {t(lang, 'retry')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 px-6 text-center">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-700 border-t-white" />
      <p className="max-w-sm text-base text-neutral-200">
        {t(lang, 'preparingInterview')}
      </p>
      <p className="max-w-sm text-sm text-neutral-400">
        {t(lang, 'preparingInterviewHint')}
      </p>
    </div>
  )
}